import { useState } from "react"
import SectionHeader from '../gdk/SectionHeader/SectionHeader';
import FileUploadFileSelection from './FileUploadFileSelection';
import FileUploadSubmitting from './FileUploadSubmitting';
import FileUploadComplete from './FileUploadComplete';
import { FileUploadState } from './FileUploadState';
import FileUploadSubmissionError from "./FileUploadSubmissionError";
import FileUploadSubmissionWarning from "./FileUploadCompleteWarning";

/**
 * File upload form/drop box.
 * @param {Object} parameters
 * @param {Object} parameters.fileUploadParameters
 * @param {String[]} parameters.fileUploadParameters.allowedFileExtensions An array of file extensions, e.g. ".jpg", ".txt", etc.
 * @param {Number} parameters.fileUploadParameters.minFileSize Minimum file size in KiB.
 * @param {Number} parameters.fileUploadParameters.maxFileSize Maximum file size in KiB.
 * @param {Number} parameters.fileUploadParameters.maxCombinedFileSize Maximum size of all files combined in KiB.
 * @param {Number} parameters.fileUploadParameters.maxFileCount Maximum number of files.
 * @param {Object[]} parameters.fileUploadParameters.uploadCategories Upload Category options. Optional.
 * @param {String} parameters.fileUploadParameters.uploadCategories.text Upload Category option display text.
 * @param {String} parameters.fileUploadParameters.uploadCategories.value Upload Category option value.
 */
export default function FileUploadForm({
    fileUploadState,
    setFileUploadState = () => { },
    onFileUploadSubmit = () => { },
    fileUploadParameters,
    progress,
    displayParameters
}) {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [errorMessage, setErrorMessage] = useState();

    const completeFileLabel = (Array.from(selectedFiles).length > 0 ? (Array.from(selectedFiles).length + " ") : "") + (Array.from(selectedFiles).length > 1 ? "files" : "file");

    const defaultDisplayParameters = {
        submittingTopText: <h3>Submitting {Array.from(selectedFiles).length} {Array.from(selectedFiles).length > 1 ? "files" : "file"}</h3>,
        submittingSubtext: <p>Feel free to navigate away from this page while {Array.from(selectedFiles).length > 1 ? "files are" : "file is"} being submitted.</p>,
        completeTopText: <h3>You've submitted {completeFileLabel}.</h3>,
        errorTopText: <h3>Submission Error</h3>,
        uploadHeader: <h3>Selected {Array.from(selectedFiles).length > 1 ? "Files" : "File"}</h3>,
        submittingHeader: <h3>Your {Array.from(selectedFiles).length > 1 ? "uploads" : "upload"}</h3>,
        completeHeader: <h3>Your {Array.from(selectedFiles).length > 1 ? "uploads" : "upload"}</h3>,
        errorHeader: <h3>Failed Upload</h3>,
        strokeSeparator: true,
        bottomSubmitButton: false,
        submitButtonText: `Submit All ${Array.from(selectedFiles).length > 1 ? "Files" : "File"}`,
        submissionErrorAlertContent: <li className="alert-content">
            <div className="h4">There was problem uploading your {Array.from(selectedFiles).length > 1 ? "files" : "file"}.</div>
            <p>Please try again.</p>
        </li>
    };

    displayParameters = {
        ...defaultDisplayParameters,
        ...displayParameters
    }

    return (
        <>
            {errorMessage &&
                <SectionHeader icon="icon-alert" header={"Important Notice"} subText={errorMessage} />
            }
            {(fileUploadState === FileUploadState.Upload || fileUploadState == FileUploadState.Error) &&
                <FileUploadFileSelection
                    setErrorMessage={setErrorMessage}
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    fileUploadState={fileUploadState}
                    setFileUploadState={setFileUploadState}
                    fileUploadParameters={fileUploadParameters}
                    displayParameters={displayParameters}
                />
            }
            {fileUploadState === FileUploadState.Submit &&
                <FileUploadSubmitting
                    selectedFiles={selectedFiles}
                    onFileUploadSubmit={onFileUploadSubmit}
                    fileUploadParameters={fileUploadParameters}
                    progress={progress}
                    displayParameters={displayParameters}
                />
            }
            {fileUploadState === FileUploadState.Complete &&
                <FileUploadComplete
                    selectedFiles={selectedFiles}
                    setFileUploadState={setFileUploadState}
                    fileUploadParameters={fileUploadParameters}
                    displayParameters={displayParameters}
                    progress={progress}
                />
            }
            {(fileUploadState == FileUploadState.SubmissionError) &&
                <FileUploadSubmissionError
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    setFileUploadState={setFileUploadState}
                    fileUploadParameters={fileUploadParameters}
                    displayParameters={displayParameters}
                />
            }
            {(fileUploadState == FileUploadState.SubmissionWarning) &&
                <FileUploadSubmissionWarning
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    setFileUploadState={setFileUploadState}
                    fileUploadParameters={fileUploadParameters}
                    displayParameters={displayParameters}
                    progress={progress}
                />
            }
        </>
    );
};
